import { useState, useEffect } from 'react';
import postGetConditionSources from '../functions/API/postGetConditionSources.js';
import postUpdateStep from '../functions/API/postUpdateStep.js';
import { toast } from '../lib/toast.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Plus, X, Zap, Loader2 } from 'lucide-react';

const CONDITION_OPERATORS = [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'in', label: 'is one of' },
    { value: 'not_in', label: 'is not one of' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
];

export default function AutoOutcomeForm({ setVisible, refresh, refreshValue, taskFlowId, sourceStep, editMode = false, ruleIndex = null }) {
    const [autoOutcomeSources, setAutoOutcomeSources] = useState([]);
    const [loadingSources, setLoadingSources] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const existingRule = editMode && ruleIndex != null && sourceStep.auto_outcome_rules?.[ruleIndex]
        ? sourceStep.auto_outcome_rules[ruleIndex]
        : null;

    const [conditions, setConditions] = useState(() => {
        if (existingRule) {
            return existingRule.conditions.map(c => ({
                source_step_id: c.source_step_id?.toString() || '',
                source_field_id: c.source_field_id?.toString() || '',
                operator: c.operator || 'equals',
                value: c.value ?? '',
            }));
        }
        return [{ source_step_id: '', source_field_id: '', operator: 'equals', value: '' }];
    });
    const [outcome, setOutcome] = useState(existingRule?.outcome || 'positive');
    const [message, setMessage] = useState(existingRule?.message || '');

    useEffect(() => {
        fetchAutoOutcomeSources();
    }, []);

    async function fetchAutoOutcomeSources() {
        setLoadingSources(true);
        try {
            const response = await postGetConditionSources(taskFlowId, sourceStep.id, true);
            if (response.response.ok && response.data?.sources) {
                let sources = response.data.sources;

                // Ensure the source step's eligible fields appear
                if (sourceStep?.fields) {
                    const selfAlreadyIncluded = sources.some(s => s.step_id == sourceStep.id);
                    if (!selfAlreadyIncluded) {
                        const eligibleFields = sourceStep.fields.filter(f =>
                            ['DROPDOWN', 'MULTI_SELECT', 'BOOLEAN'].includes(f.field_type)
                        );
                        if (eligibleFields.length > 0) {
                            sources = [...sources, {
                                step_id: sourceStep.id,
                                step_name: (sourceStep.name || 'This step') + ' (this step)',
                                step_position: sourceStep.position ?? sources.length,
                                is_self: true,
                                fields: eligibleFields.map(f => ({
                                    field_id: f.id,
                                    field_name: f.name,
                                    field_type: f.field_type,
                                    validation_rules: f.validation_rules,
                                })),
                            }];
                        }
                    }
                }

                setAutoOutcomeSources(sources);
            }
        } catch (error) {
            console.error('Error loading auto-outcome sources:', error);
        }
        setLoadingSources(false);
    }

    function addCondition() {
        setConditions([...conditions, { source_step_id: '', source_field_id: '', operator: 'equals', value: '' }]);
    }

    function removeCondition(index) {
        if (conditions.length <= 1) return;
        setConditions(conditions.filter((_, i) => i !== index));
    }

    function updateCondition(index, field, value) {
        const updated = [...conditions];
        updated[index] = { ...updated[index], [field]: value };
        if (field === 'source_step_id') {
            updated[index].source_field_id = '';
            updated[index].value = '';
        }
        if (field === 'source_field_id') {
            updated[index].value = '';
        }
        if (field === 'operator' && (value === 'is_empty' || value === 'is_not_empty')) {
            updated[index].value = null;
        }
        setConditions(updated);
    }

    function getFieldOptions(condition) {
        const source = autoOutcomeSources.find(s => s.step_id == condition.source_step_id);
        if (!source) return [];
        const field = source.fields.find(f => f.field_id == condition.source_field_id);
        if (!field || !field.validation_rules) return [];
        if (field.field_type === 'BOOLEAN') {
            return [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
        }
        const rules = typeof field.validation_rules === 'string'
            ? JSON.parse(field.validation_rules)
            : field.validation_rules;
        return rules.options || [];
    }

    async function handleSave() {
        const validConditions = conditions.filter(c =>
            c.source_step_id && c.source_field_id && c.operator
        ).map(c => ({
            source_step_id: parseInt(c.source_step_id),
            source_field_id: parseInt(c.source_field_id),
            operator: c.operator,
            value: c.value ?? null,
        }));

        if (validConditions.length === 0) {
            toast.error('Please configure at least one complete condition.');
            return;
        }

        if (!message.trim()) {
            toast.error('Please enter an outcome message.');
            return;
        }

        setSaving(true);
        try {
            const newRule = {
                conditions: validConditions,
                outcome,
                message: message.trim(),
            };

            let finalRules;
            if (editMode && ruleIndex != null) {
                finalRules = [...(sourceStep.auto_outcome_rules || [])];
                finalRules[ruleIndex] = newRule;
            } else {
                finalRules = [...(sourceStep.auto_outcome_rules || []), newRule];
            }

            const body = {
                id: sourceStep.id,
                task_flow_id: taskFlowId,
                name: sourceStep.name,
                description: sourceStep.description,
                actor_type: sourceStep.actor_type,
                step_type: sourceStep.step_type,
                step_config: sourceStep.step_config,
                is_required: sourceStep.is_required,
                requires_previous_step_complete: sourceStep.requires_previous_step_complete,
                blocks_tenant_until_complete: sourceStep.blocks_tenant_until_complete,
                help_url: sourceStep.help_url || null,
                help_text: sourceStep.help_text || null,
                conditional_logic: sourceStep.conditional_logic || null,
                auto_outcome_rules: finalRules,
            };

            const response = await postUpdateStep(body);
            if (response.response.ok) {
                toast.success(editMode ? 'Auto-outcome rule updated.' : 'Auto-outcome rule added.');
                refresh(refreshValue + 1);
                setVisible(false);
            } else {
                toast.error('Failed to save auto-outcome rule.');
            }
        } catch {
            toast.error('Failed to save auto-outcome rule.');
        }
        setSaving(false);
    }

    async function handleDeleteRule() {
        setDeleting(true);
        try {
            const existingRules = [...(sourceStep.auto_outcome_rules || [])];
            existingRules.splice(ruleIndex, 1);

            const body = {
                id: sourceStep.id,
                task_flow_id: taskFlowId,
                name: sourceStep.name,
                description: sourceStep.description,
                actor_type: sourceStep.actor_type,
                step_type: sourceStep.step_type,
                step_config: sourceStep.step_config,
                is_required: sourceStep.is_required,
                requires_previous_step_complete: sourceStep.requires_previous_step_complete,
                blocks_tenant_until_complete: sourceStep.blocks_tenant_until_complete,
                help_url: sourceStep.help_url || null,
                help_text: sourceStep.help_text || null,
                conditional_logic: sourceStep.conditional_logic || null,
                auto_outcome_rules: existingRules.length > 0 ? existingRules : null,
            };

            const response = await postUpdateStep(body);
            if (response.response.ok) {
                toast.success('Auto-outcome rule deleted.');
                refresh(refreshValue + 1);
                setVisible(false);
            } else {
                toast.error('Failed to delete rule.');
            }
        } catch {
            toast.error('Failed to delete rule.');
        }
        setDeleting(false);
    }

    if (loadingSources) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                <span className="ml-2 text-sm text-gray-500">Loading available fields...</span>
            </div>
        );
    }

    if (autoOutcomeSources.length === 0) {
        return (
            <div className="py-8 text-center">
                <Zap className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No eligible fields</p>
                <p className="text-xs text-gray-400 mt-1">
                    Add dropdown, multi-select, or boolean fields to this step (or prior steps) to enable auto-outcome rules.
                </p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setVisible(false)}>
                    Close
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <p className="text-xs text-gray-500">
                Add a rule to automatically complete or close the request when{' '}
                <span className="font-medium text-gray-700">{sourceStep.name}</span> is submitted with specific answers.
            </p>

            {/* Conditions */}
            <div className="space-y-2">
                <span className="text-xs text-amber-700 font-medium">When this step is submitted AND...</span>
                {conditions.map((condition, index) => {
                    const sourceStepData = autoOutcomeSources.find(s => s.step_id == condition.source_step_id);
                    const sourceFields = sourceStepData?.fields || [];
                    const fieldOptions = getFieldOptions(condition);
                    const needsValue = condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty';

                    return (
                        <div key={index} className="p-2 bg-amber-50 rounded border border-amber-200">
                            <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="text-xs text-amber-600">
                                    {index === 0 ? 'If...' : 'AND...'}
                                </span>
                                {conditions.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeCondition(index)}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-1 gap-1.5">
                                <Select
                                    value={condition.source_step_id?.toString() || ''}
                                    onValueChange={(val) => updateCondition(index, 'source_step_id', val)}
                                >
                                    <SelectTrigger className="bg-white h-8 text-xs">
                                        <SelectValue placeholder="Select step..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {autoOutcomeSources.map(source => (
                                            <SelectItem key={source.step_id} value={source.step_id.toString()}>
                                                {source.is_self ? `This step (${source.step_name.replace(' (this step)', '')})` : `Step ${source.step_position + 1}: ${source.step_name}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                {condition.source_step_id && (
                                    <Select
                                        value={condition.source_field_id?.toString() || ''}
                                        onValueChange={(val) => updateCondition(index, 'source_field_id', val)}
                                    >
                                        <SelectTrigger className="bg-white h-8 text-xs">
                                            <SelectValue placeholder="Select field..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {sourceFields.map(field => (
                                                <SelectItem key={field.field_id} value={field.field_id.toString()}>
                                                    {field.field_name} ({field.field_type})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}

                                {condition.source_field_id && (
                                    <div className="flex gap-1.5">
                                        <Select
                                            value={condition.operator || 'equals'}
                                            onValueChange={(val) => updateCondition(index, 'operator', val)}
                                        >
                                            <SelectTrigger className="bg-white h-8 text-xs w-[130px] shrink-0">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CONDITION_OPERATORS.map(op => (
                                                    <SelectItem key={op.value} value={op.value}>
                                                        {op.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>

                                        {needsValue && fieldOptions.length > 0 && (
                                            <Select
                                                value={condition.value?.toString() || ''}
                                                onValueChange={(val) => updateCondition(index, 'value', val)}
                                            >
                                                <SelectTrigger className="bg-white h-8 text-xs flex-1">
                                                    <SelectValue placeholder="Select value..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {fieldOptions.map(opt => (
                                                        <SelectItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}

                                        {needsValue && fieldOptions.length === 0 && (
                                            <input
                                                type="text"
                                                value={condition.value || ''}
                                                onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                                placeholder="Value"
                                                className="flex-1 rounded-md border-0 py-1 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-amber-600"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                <button
                    type="button"
                    onClick={addCondition}
                    className="text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1"
                >
                    <Plus className="h-3 w-3" /> Add condition
                </button>
            </div>

            {/* Outcome + Message */}
            <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-amber-800">Then:</span>
                    <Select value={outcome} onValueChange={setOutcome}>
                        <SelectTrigger className="bg-white h-8 text-xs w-[180px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="positive">Complete Request</SelectItem>
                            <SelectItem value="negative">Close Request</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <label className="text-xs font-medium text-amber-800">Message:</label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={2}
                        placeholder="Message sent to the customer..."
                        className="mt-1 block w-full rounded-md border-0 py-1.5 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:leading-6"
                    />
                </div>
            </div>

            <div className="flex justify-between gap-2 pt-4 border-t">
                {editMode ? (
                    <Button variant="destructive" size="sm" onClick={handleDeleteRule} disabled={saving || deleting}>
                        {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Delete Rule
                    </Button>
                ) : <div />}
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setVisible(false)} disabled={saving || deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving || deleting}>
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {editMode ? 'Update Rule' : 'Save Rule'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
